/**
 * Order service.
 *
 * API reference: api_doc/order.md
 * Base path: /api/v1/user
 */

const ORDER_API = {
  FETCH: '/api/v1/user/order/fetch',
  DETAIL: '/api/v1/user/order/detail',
  SAVE: '/api/v1/user/order/save',
  CHECKOUT: '/api/v1/user/order/checkout',
  CHECK: '/api/v1/user/order/check',
  PAYMENT_METHODS: '/api/v1/user/order/getPaymentMethod',
  CANCEL: '/api/v1/user/order/cancel',
  COUPON_CHECK: '/api/v1/user/coupon/check',
  PLAN_FETCH: '/api/v1/user/plan/fetch'
}

const withQuery = (endpoint, params = {}) => {
  const query = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.set(key, String(value))
    }
  })

  const queryString = query.toString()
  return queryString ? `${endpoint}?${queryString}` : endpoint
}

export async function getOrders(api, filters = {}) {
  return api.call(withQuery(ORDER_API.FETCH, { status: filters.status }))
}

export async function getPlans(api) {
  return api.call(ORDER_API.PLAN_FETCH)
}

export async function getPlanDetail(api, planId) {
  return api.call(withQuery(ORDER_API.PLAN_FETCH, { id: planId }))
}

/**
 * Compatible signatures:
 * - checkCoupon(api, planId, code)
 * - checkCoupon(api, planId, code, period)
 * - checkCoupon(api, { planId, code, period })
 */
export async function checkCoupon(api, planIdOrOptions, code, period) {
  const options = typeof planIdOrOptions === 'object'
    ? planIdOrOptions
    : { planId: planIdOrOptions, code, period }

  return api.call(ORDER_API.COUPON_CHECK, 'POST', {
    code: options.code,
    plan_id: options.planId ?? options.plan_id,
    period: options.period
  })
}

export async function createOrder(api, planId, period, couponCode = null) {
  const data = {
    plan_id: planId,
    period
  }

  if (couponCode) {
    data.coupon_code = couponCode
  }

  return api.call(ORDER_API.SAVE, 'POST', data)
}

export async function getPaymentMethods(api) {
  return api.call(ORDER_API.PAYMENT_METHODS)
}

export async function payOrder(api, tradeNo, method, token) {
  try {
    const response = await api.client.request({
      url: ORDER_API.CHECKOUT,
      method: 'POST',
      data: {
        trade_no: tradeNo,
        method,
        ...(token ? { token } : {})
      }
    })

    if (response.data) {
      return response.data
    }

    throw new Error('Payment API returned an invalid response')
  } catch (error) {
    if (error.response) {
      if (error.response.status === 401) {
        throw new Error('Authentication failed, please log in again')
      }
      throw new Error(error.response.data?.message || `HTTP error: ${error.response.status}`)
    }
    throw new Error(error.message || 'Network request failed')
  }
}

export async function getOrderDetail(api, tradeNo) {
  return api.call(withQuery(ORDER_API.DETAIL, { trade_no: tradeNo }))
}

export async function checkOrderStatus(api, tradeNo) {
  return api.call(withQuery(ORDER_API.CHECK, { trade_no: tradeNo }))
}

export async function cancelOrder(api, tradeNo) {
  return api.call(ORDER_API.CANCEL, 'POST', {
    trade_no: tradeNo
  })
}

export default {
  getOrders,
  getPlans,
  getPlanDetail,
  checkCoupon,
  createOrder,
  getPaymentMethods,
  payOrder,
  getOrderDetail,
  checkOrderStatus,
  cancelOrder
}
